import { useState } from 'react';
import { useThreadEntities } from '../lib/useThreadEntities.js';
import { useThreadCitations } from '../lib/useThreadCitations.js';
import { useThreadArchivistEvents } from '../lib/useThreadArchivistEvents.js';
import { useThreadSummary } from '../lib/useThreadSummary.js';
import { useDecisions } from '../lib/useDecisions.js';
import { useCommitments } from '../lib/useCommitments.js';
import { usePinned } from '../lib/usePinned.js';

import { V2Summary } from './v2tabs/V2Summary.jsx';
import { V2Entities } from './v2tabs/V2Entities.jsx';
import { V2Memory } from './v2tabs/V2Memory.jsx';
import { V2Decisions } from './v2tabs/V2Decisions.jsx';
import { V2Commits } from './v2tabs/V2Commits.jsx';
import { V2Sources } from './v2tabs/V2Sources.jsx';
import { V2Archivist } from './v2tabs/V2Archivist.jsx';
import { V2Pinned } from './v2tabs/V2Pinned.jsx';

export function V2RightPane({ tab, onTabChange, events, threadURN, principalURN, onScrollTo, currentBranch }) {
  const [branchScope, setBranchScope] = useState('all'); // 'all' | 'this'

  const entities = useThreadEntities(events);
  const citations = useThreadCitations(events);
  // JOBE-0143.3: useThreadArchivistEvents now opens a live EventSource
  // rather than filtering from the WS event stream.
  const archivistEvs = useThreadArchivistEvents(threadURN);
  const summary = useThreadSummary(events, threadURN);
  const { decisions, loading: dLoading } = useDecisions({ principalURN, threadURN, events });
  const { commitments, loading: cLoading } = useCommitments({ principalURN, threadURN, events });
  const pinned = usePinned(threadURN);

  const memoryCitations = citations.filter(c => c.kind === 'memory');

  const TABS = [
    { name: 'summary',   label: 'Summary',   count: summary ? 1 : 0 },
    { name: 'entities',  label: 'Entities',   count: entities.length },
    { name: 'memory',    label: 'Memory',     count: memoryCitations.length },
    { name: 'decisions', label: 'Decisions',  count: decisions.length },
    { name: 'commits',   label: 'Commits',    count: commitments.length },
    { name: 'sources',   label: 'Sources',    count: citations.length },
    { name: 'archivist', label: 'Archivist',  count: archivistEvs.length },
    { name: 'pinned',    label: 'Pinned',     count: pinned.length },
  ];

  function handleStripKeyDown(e) {
    const names = TABS.map(t => t.name);
    const idx = names.indexOf(tab);
    if (e.key === 'ArrowRight' && idx < names.length - 1) {
      e.preventDefault();
      onTabChange(names[idx + 1]);
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      e.preventDefault();
      onTabChange(names[idx - 1]);
    }
  }

  const ROW1 = TABS.slice(0, 4);
  const ROW2 = TABS.slice(4);

  function tabButton({ name, label, count }) {
    return (
      <button
        key={name}
        role="tab"
        aria-selected={tab === name}
        className={`v2-tab-btn ${tab === name ? 'v2-tab-active' : ''}`}
        data-testid={`v2-tab-${name}`}
        onClick={() => onTabChange(name)}
      >
        {label}{count > 0 && <span className="v2-tab-count">{count}</span>}
      </button>
    );
  }

  return (
    <div className="v2-right-pane" data-testid="v2-right-pane">
      <div className="v2-tab-strip" data-testid="v2-tab-strip" role="tablist"
           onKeyDown={handleStripKeyDown}>
        <div className="v2-tab-row">{ROW1.map(tabButton)}</div>
        <div className="v2-tab-row">{ROW2.map(tabButton)}</div>
      </div>
      {/* JOBE-0143.4: per-branch scope toggle — only shown when a branch is active */}
      {currentBranch ? (
        <div className="v2-scope-toggle" role="group" aria-label="Scope" data-testid="v2-scope-toggle">
          <button
            aria-pressed={branchScope === 'all'}
            className={`v2-scope-btn${branchScope === 'all' ? ' v2-scope-btn--active' : ''}`}
            onClick={() => setBranchScope('all')}
            data-testid="v2-scope-all"
          >All branches</button>
          <button
            aria-pressed={branchScope === 'this'}
            className={`v2-scope-btn${branchScope === 'this' ? ' v2-scope-btn--active' : ''}`}
            onClick={() => setBranchScope('this')}
            data-testid="v2-scope-this"
          >This branch</button>
        </div>
      ) : (
        <div className="v2-tab-strip-scope" data-testid="v2-tab-scope">all branches</div>
      )}
      <div className="v2-tab-content" data-testid="v2-tab-content" role="tabpanel">
        {tab === 'summary'   && (
          <V2Summary
            summary={summary}
            entities={entities}
            commitments={commitments}
            decisions={decisions}
            citations={citations}
            archivistEvs={archivistEvs}
            events={events}
          />
        )}
        {tab === 'entities'  && <V2Entities entities={entities} />}
        {tab === 'memory'    && <V2Memory memoryCitations={memoryCitations} />}
        {tab === 'decisions' && <V2Decisions decisions={decisions} loading={dLoading} />}
        {tab === 'commits'   && <V2Commits commitments={commitments} loading={cLoading} />}
        {tab === 'sources'   && <V2Sources citations={citations} />}
        {tab === 'archivist' && <V2Archivist archivistEvs={archivistEvs} />}
        {tab === 'pinned'    && <V2Pinned pinned={pinned} threadURN={threadURN} onScrollTo={onScrollTo} />}
      </div>
    </div>
  );
}
